import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Application } from '../../models/application.model';
import { ApplicationsSummary } from '../../services/application.service';
import { ApplicationSummaryCardComponent } from '../application-summary-card/application-summary-card.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslationService } from '../../services/translation.service';
import type { ApplicationsTableTranslations } from '../../i18n/applications-table';
import en from '../../i18n/en/applications-table';
import type { FilterChangeData } from '../filter-panel/filter-panel.component';
import { TooltipModule } from 'primeng/tooltip';

export type QuickStatusFilter = 'all' | 'pending' | 'inProgress' | 'rejected';

export interface PageChangeEvent {
  pageIndex: number;
  pageSize: number;
}

@Component({
  selector: 'app-applications-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ApplicationSummaryCardComponent,
    TooltipModule,
  ],

  templateUrl: './applications-table.component.html',
  styleUrls: ['./applications-table.component.css'],
})
export class ApplicationsTableComponent
  implements OnChanges, OnInit, OnDestroy
{
  // chamba-ai-backend-fastapi#287: `applications` is now the server's
  // ALREADY paginated/searched/filtered/sorted page - this component no
  // longer re-filters it client-side (it used to, over the full
  // unpaginated array). `activeFilters` is kept only to drive the filter-
  // icon's "active" dot (hasActiveFilters) and, via restoreFilters(),
  // the FilterPanel's own restored state - not for any local filtering.
  @Input() applications: Application[] = [];
  @Input() activeFilters: {
    statuses?: string[];
    companies?: string[];
    languages?: string[];
    keywords?: string[];
    hasCV?: boolean | null;
    hasCoverLetter?: boolean | null;
    minMatchScore?: number | null;
    searchQuery?: string;
  } = {};
  /** Status counts computed server-side over the user's ENTIRE history
   * (chamba-ai-backend-fastapi#287's /summary) - segment counts must
   * reflect the full history, not just the current page. */
  @Input() summary: ApplicationsSummary | null = null;
  @Input() total = 0;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  @Output() openChat = new EventEmitter<number>();
  @Output() toggleFilters = new EventEmitter<void>();
  @Output() exportExcel = new EventEmitter<void>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() quickStatusFilterChanged = new EventEmitter<QuickStatusFilter>();
  @Output() pageChanged = new EventEmitter<PageChangeEvent>();

  searchQuery = '';
  statusFilter: QuickStatusFilter = 'all';

  translations: ApplicationsTableTranslations = en;

  // Track if we've received the initial data
  hasInitialData = false;
  
  // Trigger to force child cards to re-fetch their data
  refreshTrigger = 0;

  /** Table (dense rows) vs Card (2-column grid) - a per-viewer display
   * preference, not data, so localStorage is fine here even though the
   * app avoids it for anything sensitive. Defaults to 'card' for a
   * first-time viewer (no stored preference yet) - once the viewer picks
   * either mode via setViewMode(), that choice is what persists from then
   * on, this default no longer applies. */
  private static readonly VIEW_MODE_STORAGE_KEY = 'jt-applications-view-mode';
  viewMode: 'table' | 'card' = 'card';

  private initialLoadTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private translationService: TranslationService) {
    const current = this.translationService.getApplicationsTableTranslations();
    if (current) {
      this.translations = current;
    }

    this.translationService.onChange(() => {
      const next = this.translationService.getApplicationsTableTranslations();
      if (next) {
        this.translations = next;
      }
    });

    try {
      const stored = localStorage.getItem(ApplicationsTableComponent.VIEW_MODE_STORAGE_KEY);
      if (stored === 'table' || stored === 'card') {
        this.viewMode = stored;
      }
    } catch {
      // Private browsing / storage disabled - fall back to the default.
    }
  }

  setViewMode(mode: 'table' | 'card'): void {
    this.viewMode = mode;
    try {
      localStorage.setItem(ApplicationsTableComponent.VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // Ignore - the toggle still works for the rest of this session.
    }
  }

  get t(): ApplicationsTableTranslations {
    return this.translations;
  }

  // chamba-ai-backend-fastapi#287: segment counts read the server-computed
  // status_counts over the user's ENTIRE history (this.summary), not
  // this.applications.length - that's just the current page's size.
  get totalCount(): number {
    return this.summary?.total ?? 0;
  }

  get pendingCount(): number {
    return this.summary?.status_counts?.['Pending'] ?? 0;
  }

  get inProgressCount(): number {
    const counts = this.summary?.status_counts ?? {};
    return (counts['Submitted'] ?? 0) + (counts['Invited'] ?? 0) + (counts['Interviewed'] ?? 0);
  }

  get rejectedCount(): number {
    return this.summary?.status_counts?.['Rejected'] ?? 0;
  }

  get hasActiveFilters(): boolean {
    const f = this.activeFilters ?? {};

    if (Array.isArray(f.statuses) && f.statuses.length > 0) return true;
    if (Array.isArray(f.companies) && f.companies.length > 0) return true;
    if (Array.isArray(f.languages) && f.languages.length > 0) return true;
    if (Array.isArray(f.keywords) && f.keywords.length > 0) return true;

    if (f.hasCV !== null && f.hasCV !== undefined) return true;
    if (f.hasCoverLetter !== null && f.hasCoverLetter !== undefined) return true;
    if (f.minMatchScore !== null && f.minMatchScore !== undefined) return true;

    const query = (f.searchQuery || this.searchQuery || '').trim();
    if (query.length > 0) return true;

    return false;
  }

  // chamba-ai-backend-fastapi#287: `applications` already IS the server's
  // filtered/searched/sorted/paginated page - no client-side re-filtering
  // left to do. Kept as a passthrough getter (rather than renaming every
  // template reference) since callers outside this component (the export
  // flow, prior to chamba-ai#415) used to read this property by name.
  get filteredApplications(): Application[] {
    return this.applications;
  }

  get pageCount(): number {
    return this.pageSize > 0 ? Math.max(1, Math.ceil(this.total / this.pageSize)) : 1;
  }

  get rangeFrom(): number {
    return this.total === 0 ? 0 : this.pageIndex * this.pageSize + 1;
  }

  get rangeTo(): number {
    return Math.min(this.total, (this.pageIndex + 1) * this.pageSize);
  }

  get canGoPrev(): boolean {
    return this.pageIndex > 0;
  }

  get canGoNext(): boolean {
    return this.rangeTo < this.total;
  }

  paginationSummaryText(): string {
    return this.t.PAGINATION_SUMMARY
      .replace('{from}', String(this.rangeFrom))
      .replace('{to}', String(this.rangeTo))
      .replace('{total}', String(this.total));
  }

  goToPrevPage(): void {
    if (this.canGoPrev) {
      this.pageChanged.emit({ pageIndex: this.pageIndex - 1, pageSize: this.pageSize });
    }
  }

  goToNextPage(): void {
    if (this.canGoNext) {
      this.pageChanged.emit({ pageIndex: this.pageIndex + 1, pageSize: this.pageSize });
    }
  }

  changePageSize(size: number): void {
    if (size !== this.pageSize) {
      this.pageChanged.emit({ pageIndex: 0, pageSize: size });
    }
  }

  onSearchInputChange(value: string): void {
    this.searchQuery = value;
    this.searchChanged.emit(value);
  }

  ngOnInit(): void {
    if (Array.isArray(this.applications)) {
      this.hasInitialData = true;
      return;
    }

    this.initialLoadTimeoutId = setTimeout(() => {
      this.hasInitialData = true;
      this.initialLoadTimeoutId = null;
    }, 5000);
  }

  ngOnDestroy(): void {
    if (this.initialLoadTimeoutId) {
      clearTimeout(this.initialLoadTimeoutId);
      this.initialLoadTimeoutId = null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['applications']) {
      this.hasInitialData = true;
      if (this.initialLoadTimeoutId) {
        clearTimeout(this.initialLoadTimeoutId);
        this.initialLoadTimeoutId = null;
      }
      // Increment trigger to force child cards to re-fetch their data
      // Skip on first change (initial load) to avoid double-fetching
      if (!changes['applications'].firstChange) {
        this.refreshTrigger++;
      }
    }
  }

  openChatInterface(applicationId: number): void {
    this.openChat.emit(applicationId);
  }

  onToggleFilters(): void {
    this.toggleFilters.emit();
  }

  onExportExcel(): void {
    this.exportExcel.emit();
  }

  setStatusFilter(filter: QuickStatusFilter): void {
    this.statusFilter = filter;
    this.quickStatusFilterChanged.emit(filter);
  }
}
